/**
 * @fileoverview Homepage Route
 *
 * Purpose: Landing page for Morgan AI
 * URL: /
 * Protection: Public (no auth required)
 *
 * Sections:
 * - Hero: Main value proposition with CTA
 * - Demo: Interactive product demonstration with animated showcase
 * - Feature Cards: Product benefits
 * - Data Insights: Visual data connection with AI chat demo
 * - CTA: Final conversion section
 * - Footer: Copyright and theme switcher
 */

import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import { Route as AuthLoginRoute } from "@/routes/_app/login/_layout.index";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { useEffect } from "react";
import { usePostHog } from "@/utils/posthog";

// UI Components
import { ScrollingNav } from "./_home/-ui.scrolling-nav";

// Page Sections
import { HeroSection } from "./_home/-sections.hero";
import { DemoSection } from "./_home/-sections.demo";
import { FeatureCardsSection } from "./_home/-sections.feature-cards";
import { MissionSection } from "./_home/-sections.mission";
import { IntegrationsSection } from "./_home/-sections.integrations";
import { SecuritySection } from "./_home/-sections.security";
// Hidden for now — kept for future use
// import { DataInsightsSection } from "./_home/-sections.data-insights";
import { CTASection } from "./_home/-sections.cta";
import { FooterSection } from "./_home/-sections.footer";
import { CookieBanner } from "./_home/-ui.cookie-banner";

export const Route = createFileRoute("/")({
  component: Index,
});

function Index() {
  // Use WorkOS auth directly for nav button — resolves from local storage
  // near-instantly, avoiding the slow Convex auth round-trip.
  const { user, isLoading } = useAuth();
  const posthog = usePostHog();

  useEffect(() => {
    // Track pageview once PostHog is available
    posthog?.capture("$pageview", {
      $current_url: "/",
      page_title: "Homepage",
      section: "landing",
    });
  }, [posthog]);

  return (
    <div className="relative w-full">
      {/* Navigation - sticky across entire page */}
      <ScrollingNav
        isAuthenticated={!!user}
        isLoading={isLoading}
        loginPath={AuthLoginRoute.fullPath}
        dashboardPath={DashboardRoute.fullPath}
      />

      {/* Hero Section */}
      <HeroSection />

      {/* Demo Section */}
      <DemoSection />

      {/* Content Sections */}
      <FeatureCardsSection />
      <MissionSection />
      <IntegrationsSection />
      <SecuritySection />
      {/* <DataInsightsSection /> hidden for now */}
      <CTASection />

      {/* Footer */}
      <FooterSection />

      <CookieBanner />
    </div>
  );
}
